import { Theme } from '@radix-ui/themes';
import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';
import { Nav } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/nav';
import { Footer } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/footer';
import { PortalContainerProvider } from '@trashlab/sitesapp/src/organizations/redebins/contexts/portal-container-context';
import { SEOChecker } from '@trashlab/sitesapp/src/organizations/redebins/components/dev/seo-checker';

// RED-E-BINS SEO Configuration - Hardcoded to match original website (mn.redebins.com)
// This bypasses the database variants to ensure consistent SEO across all environments
const SEO_CONFIG = {
  title:
    'Dumpster Rental | Twin Cities | Minneapolis & St. Paul | Seven County Metro Area',
  description:
    "You're just a few clicks from getting a RED-E-BIN dumpster rental. Our system is designed for smooth online booking to quick delivery and pick up.",
  siteName: 'RED-E-BINS Minnesota',
  keywords: [
    'dumpster rental',
    'dumpster rental Minneapolis',
    'dumpster rental St. Paul',
    'dumpster rental Twin Cities',
    'residential dumpster rental',
    'commercial dumpster rental',
    '5 yard dumpster',
    '10 yard dumpster',
    'roll off dumpster',
    'junk removal',
    'waste removal',
    'construction debris',
    'Seven County Metro Area',
  ],
  ogImage: '/redebins/images/og-image.jpg',
  twitterCard: 'summary' as const,
  robots: 'max-image-preview:large',
};

export const RedebinsTemplate = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Update manifest link to use REDEBINS-specific manifest on mount
  // This ensures PWA install prompts use the correct branding
  useEffect(() => {
    const existingLink = document.querySelector('link[rel="manifest"]');
    if (existingLink) {
      existingLink.setAttribute('href', '/redebins/manifest.json');
    }
  }, []);

  // RED-E-BINS brand colors
  const primaryColor = '#E31837'; // Red
  const secondaryColor = '#333333'; // Dark Gray
  const backgroundColor = '#FFFFFF'; // White
  const accentColor = '#FFEB3B'; // Yellow

  // Get canonical URL (works on both client and server)
  const canonicalUrl =
    typeof window !== 'undefined' ? window.location.href : '';

  // JSON-LD Structured Data - WebSite schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://mn.redebins.com/#website',
    url: 'https://mn.redebins.com/',
    name: SEO_CONFIG.siteName,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://mn.redebins.com/?s={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  // JSON-LD Structured Data - LocalBusiness schema for SEO
  // Address is required per Google Rich Results validation for LocalBusiness
  // Using array @type for better categorization per Google's guidelines
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['WasteManagementCompany', 'LocalBusiness'],
    '@id': 'https://mn.redebins.com/#business',
    name: 'RED-E-BINS Minnesota',
    additionalType: 'Dumpster rental service',
    description:
      'RED-E-BINS provides dumpster rental and junk removal services in the Twin Cities metro area. Compact, driveway-friendly dumpsters for residential and commercial projects.',
    url: 'https://mn.redebins.com/',
    telephone: '+1-952-931-2577',
    email: 'customerservice@redebins.com',
    image: 'https://mn.redebins.com/redebins/images/og-image.jpg',
    logo: 'https://mn.redebins.com/redebins/images/Red-E-Bins_2026_RGB_Primary+Minnesota.svg',
    currenciesAccepted: 'USD',
    paymentAccepted: 'Cash, Credit Card',
    // Address required for Google Rich Results validation
    address: {
      '@type': 'PostalAddress',
      streetAddress: '7204 West 27th Street',
      addressLocality: 'St. Louis Park',
      addressRegion: 'MN',
      postalCode: '55426',
      addressCountry: 'US',
    },
    // Service area covers Twin Cities 7-county metro with Wikidata IDs for disambiguation
    areaServed: [
      {
        '@type': 'AdministrativeArea',
        name: 'Hennepin County',
        sameAs: 'https://www.wikidata.org/wiki/Q486229',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Ramsey County',
        sameAs: 'https://www.wikidata.org/wiki/Q491201',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Dakota County',
        sameAs: 'https://www.wikidata.org/wiki/Q111694',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Anoka County',
        sameAs: 'https://www.wikidata.org/wiki/Q110495',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Washington County',
        sameAs: 'https://www.wikidata.org/wiki/Q485408',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Scott County',
        sameAs: 'https://www.wikidata.org/wiki/Q491256',
      },
      {
        '@type': 'AdministrativeArea',
        name: 'Carver County',
        sameAs: 'https://www.wikidata.org/wiki/Q113275',
      },
    ],
    // Geo coordinates for business location (exact GMB values)
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 44.95531996466011,
      longitude: -93.37124610263096,
    },
    // Note: Opening hours removed - managed via Google My Business for single source of truth
    // Services offered
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Dumpster Rental & Junk Removal Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '5-Yard Dumpster Rental',
            description:
              'Compact 5-yard dumpster (8x4x4) perfect for small DIY projects, garage cleanouts, and heavier materials like concrete.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '10-Yard Dumpster Rental',
            description:
              'Medium 10-yard dumpster (10x5x5) ideal for larger renovations, construction debris, and whole-house cleanouts.',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Junk Removal Service',
            description:
              'Full-service junk removal for items we handle for you - furniture, appliances, yard waste, and more.',
          },
        },
      ],
    },
    // Additional business details
    sameAs: [
      'https://www.facebook.com/redebins',
      'https://www.instagram.com/redebins',
    ],
    knowsAbout: [
      'Dumpster Rental',
      'Junk Removal',
      'Waste Management',
      'Construction Debris Removal',
      'Residential Cleanouts',
      'Commercial Waste Services',
    ],
  };

  return (
    <Theme
      radius="medium"
      style={
        {
          '--primary-color': primaryColor,
          '--secondary-color': secondaryColor,
          '--background-color': backgroundColor,
          '--accent-color': accentColor,
          '--success-color': '#40C057',
          '--danger-color': '#FA5252',
          '--font-family': 'system-ui, -apple-system, sans-serif',
          // Prevent overflow:auto which breaks sticky positioning
          overflow: 'visible',
          overflowX: 'clip',
          width: '100%',
        } as React.CSSProperties
      }
    >
      <PortalContainerProvider>
        {/* Hardcoded SEO - bypasses database variants for consistency */}
        <Helmet>
          {/* Basic Meta Tags */}
          <title>{SEO_CONFIG.title}</title>
          <meta name="description" content={SEO_CONFIG.description} />
          <meta name="keywords" content={SEO_CONFIG.keywords.join(', ')} />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta charSet="utf-8" />

          {/* Robots */}
          <meta name="robots" content={SEO_CONFIG.robots} />

          {/* PWA & Branding Meta Tags - matches Google My Business name for NAP consistency */}
          <meta name="application-name" content="RED-E-BINS Minnesota" />
          <meta name="apple-mobile-web-app-title" content="RED-E-BINS Minnesota" />
          <meta name="theme-color" content="#E31837" />
          <meta name="msapplication-TileColor" content="#E31837" />

          {/* REDEBINS-specific manifest for PWA */}
          <link rel="manifest" href="/redebins/manifest.json" />

          {/* Canonical URL */}
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

          {/* Favicon - SVG primary with ICO fallback */}
          <link
            rel="icon"
            type="image/svg+xml"
            href="/redebins/images/Red-E-Bins_2026_RGB_Primary.svg"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href="/redebins/images/favicon-32x32.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href="/redebins/images/favicon-16x16.png"
          />
          <link
            rel="shortcut icon"
            href="/redebins/images/favicon.ico"
          />

          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:title" content={SEO_CONFIG.title} />
          <meta property="og:description" content={SEO_CONFIG.description} />
          <meta property="og:site_name" content={SEO_CONFIG.siteName} />
          <meta property="og:image" content={SEO_CONFIG.ogImage} />

          {/* Twitter */}
          <meta name="twitter:card" content={SEO_CONFIG.twitterCard} />
          <meta name="twitter:url" content={canonicalUrl} />
          <meta name="twitter:title" content={SEO_CONFIG.title} />
          <meta name="twitter:description" content={SEO_CONFIG.description} />
          <meta name="twitter:image" content={SEO_CONFIG.ogImage} />

          {/* Structured Data - WebSite Schema */}
          <script type="application/ld+json">
            {JSON.stringify(websiteSchema)}
          </script>

          {/* Structured Data - LocalBusiness Schema for local SEO */}
          <script type="application/ld+json">
            {JSON.stringify(localBusinessSchema)}
          </script>
        </Helmet>

        <Nav />
        <main>{children}</main>
        <Footer />
        <SEOChecker />
      </PortalContainerProvider>
    </Theme>
  );
};
